import config from '@/config';
import { LISTING_PAGE_SUFFIX, parseListingPageParam } from '../parse-listing-page-param';
import { shouldNoindexListingQuery } from '../should-noindex-listing-query';
import type { SeoResolveData } from '../../types';

export interface SeoResolveDocumentMetaPatch {
  title: string;
  description: string;
  canonicalHref: string;
  robots: string;
  openGraph: {
    title: string;
    description: string;
    url: string;
  };
  twitter: {
    card: string;
    title: string;
    description: string;
  };
}

const toAbsoluteUrl = (pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const baseUrl = config.PUBLIC_URL.replace(/\/$/, '');
  const pathName = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${baseUrl}${pathName}`;
};

/**
 * Maps /seo/resolve payload to the head fields we keep in sync on soft
 * permalink navigations (pushState), mirroring buildMetadataFromResolve.
 */
export const buildSeoResolveDocumentMetaPatch = (
  resolved: SeoResolveData,
): SeoResolveDocumentMetaPatch => {
  const canonicalHref = toAbsoluteUrl(resolved.canonical);

  return {
    title: resolved.title,
    description: resolved.description,
    canonicalHref,
    robots: resolved.robots,
    openGraph: {
      title: resolved.title,
      description: resolved.description,
      url: canonicalHref,
    },
    twitter: {
      card: 'summary_large_image',
      title: resolved.title,
      description: resolved.description,
    },
  };
};

const upsertMetaByAttribute = (
  attributeName: 'name' | 'property',
  attributeValue: string,
  content: string,
): void => {
  const selector = `meta[${attributeName}="${attributeValue}"]`;
  let metaElement = document.head.querySelector<HTMLMetaElement>(selector);

  if (!metaElement) {
    metaElement = document.createElement('meta');
    metaElement.setAttribute(attributeName, attributeValue);
    document.head.appendChild(metaElement);
  }

  metaElement.setAttribute('content', content);
};

const upsertCanonicalLink = (href: string): void => {
  let linkElement = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.setAttribute('rel', 'canonical');
    document.head.appendChild(linkElement);
  }

  linkElement.setAttribute('href', href);
};

const readBrowserListingQuery = (): { search?: string; sort?: string; page?: string } => {
  if (typeof window === 'undefined') {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  return {
    search: searchParams.get('search') ?? undefined,
    sort: searchParams.get('sort') ?? undefined,
    page: searchParams.get('page') ?? undefined,
  };
};

const resolveBrowserPageNumber = (page?: string | number): number => {
  if (typeof page === 'number') {
    return page > 1 ? page : 1;
  }
  const parsed = parseListingPageParam(page);
  return parsed.status === 'ok' ? parsed.page : 1;
};

const buildSelfCanonicalFromBrowser = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const pathWithQuery = `${window.location.pathname}${window.location.search}`;
  return toAbsoluteUrl(pathWithQuery);
};

/**
 * Keeps <meta name="robots"> aligned with search/sort after soft URL updates
 * (pushState), when Next.js Metadata API does not re-run. Pagination page≥2
 * stays index,follow unless search/sort triggers noindex.
 */
export const syncListingQueryRobotsDocumentMeta = (params: {
  search?: string;
  sort?: string;
  page?: string | number;
  fallbackRobots?: string;
}): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const pageNumber = resolveBrowserPageNumber(params.page);
  const robotsContent = shouldNoindexListingQuery({
    search: params.search,
    sort: params.sort,
  })
    ? 'noindex, nofollow'
    : pageNumber >= 2
      ? 'index, follow'
      : (params.fallbackRobots ?? 'index, follow');
  upsertMetaByAttribute('name', 'robots', robotsContent);
};

/**
 * Soft pagination: append «— страница N» to title/description and set
 * self-canonical when page≥2 (Next Metadata API does not re-run on pushState).
 */
export const syncListingPaginationDocumentMeta = (params: {
  page: number;
  baseTitle?: string | null;
  baseDescription?: string | null;
}): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const pageSuffix = LISTING_PAGE_SUFFIX(params.page);
  const baseTitle = params.baseTitle?.trim() ?? '';
  const baseDescription = params.baseDescription?.trim() ?? '';

  if (baseTitle.length > 0) {
    document.title = `${baseTitle}${pageSuffix}`;
    upsertMetaByAttribute('property', 'og:title', `${baseTitle}${pageSuffix}`);
    upsertMetaByAttribute('name', 'twitter:title', `${baseTitle}${pageSuffix}`);
  }

  if (baseDescription.length > 0) {
    upsertMetaByAttribute('name', 'description', `${baseDescription}${pageSuffix}`);
    upsertMetaByAttribute('property', 'og:description', `${baseDescription}${pageSuffix}`);
    upsertMetaByAttribute('name', 'twitter:description', `${baseDescription}${pageSuffix}`);
  }

  if (params.page >= 2) {
    const selfCanonical = buildSelfCanonicalFromBrowser();
    if (selfCanonical) {
      upsertCanonicalLink(selfCanonical);
      upsertMetaByAttribute('property', 'og:url', selfCanonical);
    }
  }
};

/**
 * Patches document head after client-side SEO permalink navigation.
 * Next.js Metadata API does not re-run on history.pushState, so title /
 * description / canonical / OG / Twitter must be updated manually from resolve.
 */
export const applySeoResolveDocumentMeta = (resolved: SeoResolveData): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const metaPatch = buildSeoResolveDocumentMetaPatch(resolved);
  const listingQuery = readBrowserListingQuery();
  const pageNumber = resolveBrowserPageNumber(listingQuery.page);
  const pageSuffix = LISTING_PAGE_SUFFIX(pageNumber);
  const title = `${metaPatch.title}${pageSuffix}`;
  const description = metaPatch.description
    ? `${metaPatch.description}${pageSuffix}`
    : metaPatch.description;
  const canonicalHref =
    pageNumber >= 2
      ? buildSelfCanonicalFromBrowser() || metaPatch.canonicalHref
      : metaPatch.canonicalHref;
  const robotsContent = shouldNoindexListingQuery(listingQuery)
    ? 'noindex, nofollow'
    : pageNumber >= 2
      ? 'index, follow'
      : metaPatch.robots;

  document.title = title;
  upsertMetaByAttribute('name', 'description', description);
  upsertMetaByAttribute('name', 'robots', robotsContent);
  upsertCanonicalLink(canonicalHref);

  upsertMetaByAttribute('property', 'og:title', title);
  upsertMetaByAttribute('property', 'og:description', description);
  upsertMetaByAttribute('property', 'og:url', canonicalHref);

  upsertMetaByAttribute('name', 'twitter:card', metaPatch.twitter.card);
  upsertMetaByAttribute('name', 'twitter:title', title);
  upsertMetaByAttribute('name', 'twitter:description', description);
};
